import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useProviderAuth } from '../../context/ProviderAuthContext';
import { Eye, EyeOff, ArrowRight, Building2, KeyRound, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import MeridianLogo from '../../components/MeridianLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProviderRegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', claim_code: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Claim code request state
  const [showClaimReq, setShowClaimReq] = useState(false);
  const [claimReqForm, setClaimReqForm] = useState({ facility_name: '', contact_email: '' });
  const [claimReqLoading, setClaimReqLoading] = useState(false);
  const [claimReqError, setClaimReqError] = useState('');
  const [claimReqSuccess, setClaimReqSuccess] = useState('');

  const { providerRegister } = useProviderAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await providerRegister(form.email, form.password, form.claim_code);
      setSuccess(true);
      setTimeout(() => navigate('/provider-portal/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimRequest = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setClaimReqError('');
    if (!claimReqForm.facility_name.trim() || !claimReqForm.contact_email.trim()) {
      setClaimReqError('Please fill in all required fields.');
      return;
    }
    setClaimReqLoading(true);
    try {
      const res = await axios.post(`${API}/provider/auth/request-claim-code`, claimReqForm);
      setClaimReqSuccess(res.data.message);
      setClaimReqForm({ facility_name: '', contact_email: '' });
    } catch (err) {
      setClaimReqError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setClaimReqLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/"><MeridianLogo variant="dark" /></Link>

        <div>
          <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center mb-8">
            <KeyRound size={22} className="text-white" />
          </div>
          <h2 className="text-4xl font-medium text-white mb-6 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
            Register your facility
          </h2>
          <p className="text-[#A8B4B8] text-lg mb-8 leading-relaxed">
            Create a provider account to access your lead dashboard and explore partnership opportunities with Meridian.
          </p>
          <div className="bg-[#0A3340] border border-[#F0EEE9]/10 p-5">
            <p className="text-xs text-[#A8B4B8] uppercase tracking-widest font-semibold mb-2">Provider Claim Code</p>
            <p className="text-[#A8B4B8] text-sm leading-relaxed">
              Your Provider Claim Code is issued by Meridian during the facility onboarding process. If you don't have one, contact <span className="text-[#A8B4B8]">partners@meridiantracker.com</span> to get started.
            </p>
          </div>
        </div>

        <p className="text-[#F0EEE9]/40 text-xs">
          Already registered? <Link to="/provider-portal/login" className="text-[#A8B4B8] hover:text-[#F0EEE9]/90">Sign in &rarr;</Link>
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          {success ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#0D3D4A]/10 flex items-center justify-center mx-auto mb-4">
                <Building2 size={28} className="text-[#0D3D4A]" />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Registration submitted!</h2>
              <p className="text-stone-500 text-sm">Your account is pending review. Redirecting to login...</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Provider Portal</p>
                <h1 className="text-3xl font-medium text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Register your facility</h1>
                <p className="text-stone-500 text-sm">Create a provider account to access the Meridian lead dashboard.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="provider-register-form">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="admin@facility.com"
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                    data-testid="provider-reg-email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm({...form, password: e.target.value})}
                      placeholder="Min. 6 characters"
                      className="w-full px-4 py-3 pr-12 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                      data-testid="provider-reg-password"
                      required
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Provider Claim Code <span className="text-[#A8B4B8] font-normal">(from Meridian onboarding)</span>
                  </label>
                  <input
                    type="text"
                    value={form.claim_code}
                    onChange={e => setForm({...form, claim_code: e.target.value})}
                    placeholder="e.g. CS-PROV-2025-XXXXX"
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors font-mono"
                    data-testid="provider-claim-code"
                    required
                  />
                  <p className="text-xs text-[#A8B4B8] mt-1">
                    Don't have a code?{' '}
                    <button
                      type="button"
                      onClick={() => { setShowClaimReq(!showClaimReq); setClaimReqSuccess(''); setClaimReqError(''); }}
                      className="text-[#0D3D4A] font-medium hover:underline inline-flex items-center gap-0.5"
                      data-testid="toggle-claim-request"
                    >
                      Request one
                      {showClaimReq ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  </p>

                  {/* Collapsible claim code request form */}
                  {showClaimReq && (
                    <div
                      className="mt-3 border border-[#0D3D4A]/20 bg-white p-4 space-y-3"
                      data-testid="claim-request-panel"
                    >
                      {claimReqSuccess ? (
                        <div className="flex items-start gap-2.5 py-1" data-testid="claim-request-success">
                          <CheckCircle size={15} className="text-[#0D3D4A] mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-[#0D3D4A] font-medium leading-snug">{claimReqSuccess}</p>
                        </div>
                      ) : (
                        <div className="space-y-3" data-testid="claim-request-form">
                          <p className="text-xs text-stone-500 leading-relaxed">
                            Enter your facility details and we'll send your claim code within 1 business day.
                          </p>
                          <div>
                            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1">
                              Facility Name
                            </label>
                            <input
                              type="text"
                              value={claimReqForm.facility_name}
                              onChange={e => setClaimReqForm({ ...claimReqForm, facility_name: e.target.value })}
                              placeholder="e.g. Oklahoma Surgical Center"
                              className="w-full px-3 py-2.5 border border-[#A8B4B8] bg-[#F0EEE9] text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                              data-testid="claim-req-facility"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1">
                              Contact Email
                            </label>
                            <input
                              type="email"
                              value={claimReqForm.contact_email}
                              onChange={e => setClaimReqForm({ ...claimReqForm, contact_email: e.target.value })}
                              placeholder="admin@yourfacility.com"
                              className="w-full px-3 py-2.5 border border-[#A8B4B8] bg-[#F0EEE9] text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                              data-testid="claim-req-email"
                              required
                            />
                          </div>
                          {claimReqError && (
                            <p className="text-xs text-red-600" data-testid="claim-request-error">{claimReqError}</p>
                          )}
                          <button
                            type="button"
                            onClick={handleClaimRequest}
                            disabled={claimReqLoading}
                            className="w-full bg-[#0D3D4A] text-white py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#0E4557] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                            data-testid="claim-request-submit"
                          >
                            {claimReqLoading ? 'Sending…' : 'Request Access'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="provider-reg-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="provider-reg-submit"
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                  {!loading && <ArrowRight size={16} />}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-stone-500">
                  Already have an account?{' '}
                  <Link to="/provider-portal/login" className="text-[#0D3D4A] font-medium hover:text-[#0D3D4A]">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
